import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Leaf, CheckCircle, ChevronRight, Phone,
  CreditCard, Shield, Percent, Clock, Users, Sparkles,
  PoundSterling, BadgeCheck
} from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import { fadeUp, staggerContainer, staggerItem, pageTransition, viewportConfig } from '../components/AnimationVariants';

const TABS = ['Membership Plans', 'Patient Finance'];


const PLANS = [
  {
    name: 'Essential Smile',
    price: '£11.99',
    period: '/month',
    tagline: 'Your core preventive dental care plan',
    color: 'border-sage',
    highlight: false,
    features: [
      '1 full dental examination per year',
      '1 hygiene appointment with Airflow polishing per year',
      '10% discount on eligible dental treatments',
      'Priority reminders for routine appointments',
    ],
  },
  {
    name: 'Complete Smile',
    price: '£14.99',
    period: '/month',
    tagline: 'Everything in Essential, plus emergency cover',
    color: 'border-sage',
    highlight: false,
    features: [
      'Everything in Essential Smile, plus:',
      '1 emergency dental assessment per year',
      'Priority access for urgent appointments',
    ],
  },
  {
    name: 'Premium Smile',
    price: '£19.99',
    period: '/month',
    tagline: 'Enhanced preventive care for complete peace of mind',
    color: 'border-forest',
    highlight: true,
    badge: 'Most Popular',
    features: [
      '2 full dental examinations per year',
      '2 hygiene appointments with Airflow polishing per year',
      '1 emergency dental assessment per year',
      '10% discount on eligible dental treatments',
      'Priority access to appointments',
    ],
  },
  {
    name: 'Platinum Family Smile',
    price: '£24.99',
    period: '/month',
    tagline: 'Comprehensive plan for individuals and small families',
    color: 'border-gold',
    highlight: false,
    features: [
      '2 full dental examinations per year',
      '2 hygiene appointments with Airflow polishing per year',
      '1 emergency dental assessment per year',
      '10% discount on eligible dental treatments',
      'Children under 12 join FREE (up to 2 children)',
      'Priority access to appointments',
    ],
  },
  {
    name: 'Family Bundle Plan',
    price: '£45.99',
    period: '/month',
    tagline: 'The complete family dental care solution',
    color: 'border-gold',
    highlight: false,
    badge: 'Best Value',
    features: [
      'Up to 2 adults covered',
      '2 dental examinations per adult per year',
      '2 Airflow hygiene appointments per adult per year',
      '1 emergency dental assessment per adult per year',
      '10% discount on eligible dental treatments',
      'Children under 12 join FREE',
      'Covers up to 4 family members in total',
      'Single monthly payment for the whole family',
    ],
  },
];

const FINANCE_STEPS = [
  { step: '01', title: 'Free consultation', desc: 'Discuss your treatment plan and get a clear cost breakdown during your appointment.' },
  { step: '02', title: 'Choose your plan', desc: 'Select 0% or low-APR finance to spread the cost over a term that suits your budget.' },
  { step: '03', title: 'Quick application', desc: 'Complete a short online application — most patients receive a decision in minutes.' },
  { step: '04', title: 'Start treatment', desc: 'Once approved, your treatment begins immediately with no hidden fees.' },
];

const FINANCE_OPTIONS = [
  {
    title: '0% Interest Finance',
    duration: 'Up to 12 months',
    apr: '0% APR',
    desc: 'Spread the cost of your treatment over 12 monthly payments with absolutely no interest added. Available on treatments from £500.',
    icon: Percent,
    tag: 'Most Popular',
    tagColor: 'bg-gold text-white',
  },
  {
    title: 'Low-Rate Finance',
    duration: '24 – 60 months',
    apr: 'From 9.9% APR',
    desc: 'For larger treatment plans, spread the cost over up to 5 years with competitive interest rates. Ideal for implants, full makeovers, and orthodontics.',
    icon: Clock,
    tag: 'Flexible',
    tagColor: 'bg-sage text-white',
  },
  {
    title: 'Family Finance',
    duration: 'Flexible',
    apr: 'Combined plans',
    desc: 'Combine multiple family members\' treatments into a single finance agreement. Simplify your payments while getting the whole family the care they need.',
    icon: Users,
    tag: 'Family',
    tagColor: 'bg-forest text-white',
  },
];


function MembershipPlans() {
  return (
    <div>
      <motion.div className="text-center mb-10"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <p className="section-subtitle justify-center"><Leaf className="w-4 h-4" /> Preventive First</p>
        <h2 className="section-title mb-3">Dental Membership Plans</h2>
        <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
          Our membership plans are not insurance — they're a simple, direct way to spread the cost of your routine care and save on treatments. No waiting periods, no claim forms, no exclusions.
        </p>
      </motion.div>

      <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10"
        variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        {PLANS.map((plan) => (
          <motion.div key={plan.name} variants={staggerItem} className="relative">
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <span className="bg-gold text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-md whitespace-nowrap">
                  {plan.badge}
                </span>
              </div>
            )}
            <motion.div
              className={`card-wood h-full flex flex-col p-6 border-t-4 ${plan.color} ${plan.highlight ? 'ring-2 ring-forest/20' : ''}`}
              whileHover={{ y: -4, boxShadow: '0 20px 48px rgba(45,90,61,0.15)' }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}>
              <h3 className="font-serif text-forest text-xl font-semibold mb-1">{plan.name}</h3>
              <p className="text-gray-500 text-xs mb-5 leading-snug">{plan.tagline}</p>

              <div className="flex items-end gap-1 mb-6">
                <span className="text-4xl font-serif font-bold text-forest">{plan.price}</span>
                <span className="text-gray-400 text-sm mb-1">{plan.period}</span>
              </div>

              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex gap-2 items-start">
                    <CheckCircle className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact"
                className={`inline-flex items-center justify-center gap-2 w-full py-3 px-6 rounded-full font-semibold text-sm transition-all duration-300 ${
                  plan.highlight
                    ? 'bg-forest text-white hover:bg-forest-dark shadow-md hover:shadow-lg hover:-translate-y-0.5'
                    : 'border-2 border-forest text-forest hover:bg-forest hover:text-white'
                }`}>
                Join Now <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>

      {/* All plans include */}
      <motion.div className="card-wood p-8"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <h4 className="font-serif text-forest text-lg font-semibold mb-6 text-center">All Membership Plans Include</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { icon: Leaf, text: 'Focus on prevention and oral health' },
            { icon: PoundSterling, text: 'Spread the cost with affordable monthly payments' },
            { icon: Percent, text: 'Exclusive discounts on dental treatments' },
            { icon: Clock, text: 'Priority access to appointments' },
            { icon: BadgeCheck, text: 'Professional care from our experienced team' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.text} className="flex flex-col items-center text-center gap-2 p-4 rounded-2xl bg-cream">
                <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-forest" />
                </div>
                <span className="text-gray-600 text-xs leading-snug">{item.text}</span>
              </div>
            );
          })}
        </div>
        <p className="text-center text-gold font-serif font-semibold text-sm mt-6 italic">
          Invest in your smile, your health, and your confidence — we're here to help you smile for life.
        </p>
      </motion.div>
    </div>
  );
}

function PatientFinance() {
  return (
    <div>
      <motion.div className="text-center mb-10"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <p className="section-subtitle justify-center"><Leaf className="w-4 h-4" /> Flexible Payments</p>
        <h2 className="section-title mb-3">Patient Finance</h2>
        <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
          We want cost to never be a barrier to the care you need. Our finance options let you spread payments into manageable monthly amounts — whether it's a single crown or a complete smile transformation.
        </p>
      </motion.div>

      {/* Finance options */}
      <motion.div className="grid md:grid-cols-3 gap-6 mb-10"
        variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        {FINANCE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <motion.div key={opt.title} variants={staggerItem}>
              <motion.div
                className="card-wood p-6 h-full flex flex-col"
                whileHover={{ y: -4, boxShadow: '0 20px 48px rgba(45,90,61,0.15)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-sage-pale flex items-center justify-center">
                    <Icon className="w-6 h-6 text-forest" />
                  </div>
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${opt.tagColor}`}>{opt.tag}</span>
                </div>
                <h3 className="font-serif text-forest text-xl font-semibold mb-1">{opt.title}</h3>
                <div className="flex gap-3 mb-3">
                  <span className="text-xs bg-cream px-2.5 py-1 rounded-full text-gray-500 font-medium border border-cream-deeper">{opt.duration}</span>
                  <span className="text-xs bg-gold/10 px-2.5 py-1 rounded-full text-gold font-semibold border border-gold/20">{opt.apr}</span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed flex-1">{opt.desc}</p>
              </motion.div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* How it works */}
      <motion.div className="bg-white rounded-3xl p-8 mb-8 border border-cream-deeper"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <h3 className="font-serif text-forest text-2xl font-semibold text-center mb-8">How it works</h3>
        <div className="grid md:grid-cols-4 gap-6">
          {FINANCE_STEPS.map((step) => (
            <div key={step.step} className="text-center">
              <div className="w-12 h-12 rounded-full bg-sage-pale flex items-center justify-center mx-auto mb-4">
                <span className="font-serif text-forest font-bold text-sm">{step.step}</span>
              </div>
              <h4 className="font-serif text-forest font-semibold mb-2 text-sm">{step.title}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Example calculations */}
      <motion.div className="mb-8"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <h3 className="font-serif text-forest text-xl font-semibold mb-5">Example monthly payments</h3>
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-2xl overflow-hidden border border-cream-deeper">
            <thead>
              <tr className="bg-forest text-white">
                <th className="text-left px-5 py-3.5 text-sm font-semibold">Treatment</th>
                <th className="text-right px-5 py-3.5 text-sm font-semibold">Total Cost</th>
                <th className="text-right px-5 py-3.5 text-sm font-semibold">0% / 12mo</th>
                <th className="text-right px-5 py-3.5 text-sm font-semibold hidden sm:table-cell">9.9% / 24mo</th>
                <th className="text-right px-5 py-3.5 text-sm font-semibold hidden md:table-cell">9.9% / 48mo</th>
              </tr>
            </thead>
            <tbody>
              {[
                { treatment: 'Single Dental Crown', total: '£695', m12: '£57.92', m24: '£32.19', m48: '£17.52' },
                { treatment: 'Porcelain Veneers (4 teeth)', total: '£3,180', m12: '£265.00', m24: '£147.29', m48: '£80.19' },
                { treatment: 'Dental Implant (full)', total: '£2,995', m12: '£249.58', m24: '£138.72', m48: '£75.52' },
                { treatment: 'Invisalign Moderate', total: '£2,995', m12: '£249.58', m24: '£138.72', m48: '£75.52' },
                { treatment: 'Full Smile Makeover', total: '£5,795', m12: '—', m24: '£268.32', m48: '£146.02' },
              ].map((row, i) => (
                <tr key={i} className={`border-t border-cream-deeper ${i % 2 === 0 ? 'bg-cream/30' : 'bg-white'}`}>
                  <td className="px-5 py-3.5 text-sm text-gray-700">{row.treatment}</td>
                  <td className="px-5 py-3.5 text-sm text-right font-semibold text-forest">{row.total}</td>
                  <td className="px-5 py-3.5 text-sm text-right text-gray-600">{row.m12}</td>
                  <td className="px-5 py-3.5 text-sm text-right text-gray-600 hidden sm:table-cell">{row.m24}</td>
                  <td className="px-5 py-3.5 text-sm text-right text-gray-600 hidden md:table-cell">{row.m48}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-400 text-xs mt-3 leading-relaxed">
          Representative example: 9.9% APR. Monthly payments are illustrative only. Subject to status and credit approval. Finance provided by a third-party lender. Woodlands Centre for Dentistry is a credit broker, not a lender.
        </p>
      </motion.div>

      {/* FAQs */}
      <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
        <h3 className="font-serif text-forest text-xl font-semibold mb-5">Finance FAQs</h3>
        <div className="space-y-3">
          {[
            {
              q: 'Is a credit check required?',
              a: 'Yes, a standard credit check is carried out by our finance partner. Most patients with a reasonable credit history are approved. A soft search is performed first so there is no impact to your credit score if you do not proceed.',
            },
            {
              q: 'Can I pay off my plan early?',
              a: 'Yes. You can settle your balance early at any time with no early repayment penalty.',
            },
            {
              q: 'What is the minimum amount I can finance?',
              a: 'Finance is available on treatments from £500. For smaller amounts, we accept all major debit and credit cards, as well as interest-free monthly instalments for Membership Plan holders.',
            },
            {
              q: 'Can I combine finance with a Membership Plan discount?',
              a: 'Absolutely. Your membership discount is applied to the treatment cost first, and you can then finance the remaining balance.',
            },
          ].map((faq, i) => (
            <div key={i} className="card-wood p-5">
              <h4 className="font-semibold text-forest text-sm mb-2">{faq.q}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default function FeesPlans() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> No Surprises
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            Fees & Plans
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed max-w-2xl mx-auto mb-8"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            Transparent pricing with no hidden costs. Browse our full fee list, explore membership plan savings, or find out how our flexible finance options can make your ideal treatment affordable.
          </motion.p>
          <motion.div className="flex flex-wrap gap-3 justify-center"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.4 }}>
            <Link to="/contact" className="btn-primary text-sm py-2.5">
              <Phone className="w-4 h-4" /> Request a Quote
            </Link>
            <a href="tel:+442088665556" className="btn-outline-white text-sm py-2.5">
              <Phone className="w-4 h-4" /> 020 8866 5556
            </a>
          </motion.div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="bg-white border-b border-cream-deeper">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <motion.div className="flex flex-wrap justify-center gap-6 md:gap-12"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {[
              { icon: Shield, text: 'No Hidden Fees' },
              { icon: CreditCard, text: 'Flexible Finance' },
              { icon: BadgeCheck, text: 'Written Treatment Plans' },
              { icon: PoundSterling, text: 'Membership Savings' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <motion.div key={item.text} variants={staggerItem} className="flex items-center gap-2.5 text-gray-600">
                  <Icon className="w-5 h-5 text-forest" />
                  <span className="text-sm font-medium">{item.text}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Tab navigation */}
      <section className="bg-cream sticky top-[56px] z-30 border-b border-cream-deeper shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 py-2" role="tablist" aria-label="Fees and plans sections">
            {TABS.map((tab, i) => (
              <button
                key={tab}
                role="tab"
                aria-selected={activeTab === i}
                aria-controls={`tabpanel-${i}`}
                id={`tab-${i}`}
                onClick={() => setActiveTab(i)}
                className={`relative px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 flex-1 md:flex-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest ${
                  activeTab === i ? 'text-white' : 'text-gray-600 hover:text-forest'
                }`}>
                {activeTab === i && (
                  <motion.span layoutId="tab-pill" className="absolute inset-0 bg-forest rounded-full"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }} />
                )}
                <span className="relative z-10">{tab}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab content */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              role="tabpanel"
              id={`tabpanel-${activeTab}`}
              aria-labelledby={`tab-${activeTab}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}>
              {activeTab === 0 && <MembershipPlans />}
              {activeTab === 1 && <PatientFinance />}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-16 bg-cream">
        <div className="container-max">
          <motion.div className="bg-forest rounded-3xl p-8 md:p-12 text-white text-center"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <h2 className="font-serif text-3xl font-semibold mb-3">Not sure which option suits you?</h2>
            <p className="text-cream/75 mb-7 max-w-lg mx-auto">
              Book a free consultation and we'll walk you through your treatment options, costs, and the best way to fund the care you need.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="btn-primary">
                <Sparkles className="w-4 h-4" /> Book Free Consultation
              </Link>
              <a href="tel:+442088665556" className="btn-outline-white">
                <Phone className="w-4 h-4" /> Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
